import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, LogOut } from 'lucide-react';
import { founderApi } from '@/lib/founderApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { FounderFrame } from '@/components/founder/FounderFrame';
import { StageNavigator } from '@/components/founder/StageNavigator';
import { WorkspacePanels } from '@/components/founder/WorkspacePanels';

export default function FounderWorkspace() {
  usePageMeta({ title: 'Founder Workspace', path: '/founder/workspace', robots: 'noindex' });
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);

  useEffect(() => {
    founderApi.workspace().then(setWs).catch(() => navigate('/founder/login', { replace: true }));
  }, [navigate]);

  const signOut = async () => {
    await founderApi.logout().catch(() => {});
    navigate('/founder/login', { replace: true });
  };

  if (!ws) {
    return (
      <FounderFrame testid="founder-workspace-loading">
        <Loader2 className="animate-spin text-gold" />
      </FounderFrame>
    );
  }
  const bp = ws.blueprints[0];
  const resume = bp && (bp.last_stage && ['discover', 'define', 'design', 'calculate', 'decide', 'launch'].includes(bp.last_stage) ? bp.last_stage : 'discover');

  return (
    <FounderFrame eyebrow="ORIGENNE™ · Founder Workspace" testid="founder-workspace-page" wide>
      <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-matte leading-[1.02]">
        Welcome, <em className="italic text-taupe">{ws.customer.first_name || 'Founder'}.</em>
      </h1>
      <p className="mt-4 text-[13px] text-matte/55" data-testid="founder-workspace-powered-by">Founder Blueprint Builder&trade; · Powered by ORIGENNE&trade;, the digital headquarters of Toia Wallace</p>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-matte/60" data-testid="founder-workspace-email">{ws.customer.email}</p>
        <button onClick={signOut} className="btn-ghost" data-testid="founder-workspace-logout-button">
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      {!bp ? (
        <div className="mt-14 rounded-2xl bg-white border border-matte/10 p-8" data-testid="founder-workspace-no-access">
          <h2 className="font-serif-display text-3xl text-matte">No active blueprint</h2>
          <p className="mt-3 text-matte/70 text-sm leading-relaxed">
            This account doesn&rsquo;t currently have active Founder Blueprint Builder&trade; access. If you believe this is a mistake,
            please write to iamtoiawallace@gmail.com.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-12 rounded-2xl border border-gold/40 bg-white p-6 md:p-8" data-testid="founder-workspace-build-notice">
            <div className="eyebrow !text-gold mb-3">All six stages are open</div>
            <p className="text-matte/75 text-sm leading-relaxed max-w-3xl">
              Your answers save automatically and carry forward between stages. Return to any stage to edit it, view your consolidated blueprint, or download it as a PDF at any time.
            </p>
            {resume && (
              <Link to={`/founder/blueprint/${bp.id}/${resume}`} className="btn-primary mt-6" data-testid="founder-workspace-resume-button">
                {bp.last_stage ? 'Resume where I left off' : 'Start Stage 01'}
              </Link>
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/founder/blueprint/${bp.id}`} className="btn-ghost" data-testid="founder-workspace-view-blueprint">View Full Blueprint</Link>
              <a href={founderApi.pdfUrl(bp.id)} className="btn-ghost" data-testid="founder-workspace-download-pdf">Download PDF</a>
            </div>
          </div>
          <section className="mt-14" data-testid="founder-workspace-blueprint">
            <h2 className="font-serif-display text-3xl text-matte">{bp.title}</h2>
            <p className="mt-2 text-sm text-matte/60">Created {new Date(bp.created_at).toLocaleDateString()}</p>
            <StageNavigator bid={bp.id} stages={ws.stages} status={bp.stage_status} />
          </section>
          <WorkspacePanels insights={bp.insights} />
        </>
      )}
    </FounderFrame>
  );
}
